import { useEffect, useState } from "react";
import { api, errText, photoUrl } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Skeleton } from "../../components/ui/skeleton";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { toast } from "sonner";
import { sellerTypeClass } from "../../lib/badgeColors";
import { ShieldCheck, BadgeCheck, Globe, Check, X, FileText, Building2, Search, Trash2, ExternalLink } from "lucide-react";

function BusinessCleanup() {
  const [biz, setBiz] = useState(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [working, setWorking] = useState(false);
  const load = () => api.get("/admin/businesses").then((r) => { setBiz(r.data.businesses); setSelected({}); }).catch((e) => toast.error(errText(e)));
  useEffect(() => { load(); }, []);

  const needle = q.trim().toLowerCase();
  const filtered = (biz || []).filter((b) => !needle ||
    (b.name || "").toLowerCase().includes(needle) ||
    (b.username || "").toLowerCase().includes(needle) ||
    (b.email || "").toLowerCase().includes(needle) ||
    (b.tagline || "").toLowerCase().includes(needle) ||
    (b.website || "").toLowerCase().includes(needle));

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const allVisibleSelected = filtered.length > 0 && filtered.every((b) => selected[b.user_id]);
  const toggleAll = () => {
    const next = { ...selected };
    if (allVisibleSelected) filtered.forEach((b) => { delete next[b.user_id]; });
    else filtered.forEach((b) => { next[b.user_id] = true; });
    setSelected(next);
  };

  const removeIds = async (ids) => {
    if (!ids.length) return;
    setWorking(true);
    try {
      const { data } = await api.post("/admin/businesses/bulk-unverify", { user_ids: ids });
      toast.success(`Removed ${data.removed_count} from directory`);
      setConfirmOpen(false);
      load();
    } catch (e) { toast.error(errText(e)); }
    finally { setWorking(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="business-cleanup">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div>
          <p className="font-semibold text-[#12234A] text-sm flex items-center gap-1.5"><Building2 size={16} className="text-[#F5551E]" /> Verified business directory</p>
          <p className="text-xs text-slate-500 mt-0.5">Drop the Verified Business badge to remove leftover test accounts from the public directory. Their account and listings stay intact.</p>
        </div>
        <div className="relative shrink-0 w-full sm:w-64">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="business-cleanup-search" placeholder="Filter by name, email, website…" className="h-9 pl-9 rounded-xl" />
        </div>
      </div>

      {biz === null ? (
        <div className="space-y-2 mt-4">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-14 rounded-xl" />)}</div>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-8" data-testid="business-cleanup-empty">{biz.length === 0 ? "No verified businesses yet." : "No businesses match your filter."}</p>
      ) : (
        <>
          <div className="flex items-center justify-between mt-4 mb-1 px-1">
            <label className="flex items-center gap-2 text-xs font-medium text-slate-600 cursor-pointer">
              <input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} data-testid="business-cleanup-select-all" className="h-4 w-4 rounded accent-[#F5551E]" />
              Select all shown ({filtered.length})
            </label>
            {selectedIds.length > 0 && (
              <Button onClick={() => setConfirmOpen(true)} data-testid="business-cleanup-bulk-remove" className="h-8 rounded-xl bg-red-600 hover:bg-red-700 text-white gap-1.5 text-xs"><Trash2 size={14} /> Remove {selectedIds.length} from directory</Button>
            )}
          </div>
          <div className="divide-y divide-slate-100 border border-slate-100 rounded-xl overflow-hidden">
            {filtered.map((b) => (
              <div key={b.user_id} className="flex items-center gap-3 p-3 hover:bg-slate-50" data-testid={`business-row-${b.user_id}`}>
                <input type="checkbox" checked={!!selected[b.user_id]} onChange={() => setSelected((s) => ({ ...s, [b.user_id]: !s[b.user_id] }))} data-testid={`business-check-${b.user_id}`} className="h-4 w-4 rounded accent-[#F5551E]" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-[#12234A] text-sm truncate">{b.name || b.username || "—"}</span>
                    <Badge className={`${sellerTypeClass(b.seller_type)} text-[10px]`} data-testid={`business-type-${b.user_id}`}>{b.seller_type}</Badge>
                    <span className="text-[11px] text-slate-400">{b.active_listings} active listing{b.active_listings === 1 ? "" : "s"}</span>
                  </div>
                  <p className="text-xs text-slate-400 truncate">{b.email || b.username}{b.website ? <> · <a href={b.website} target="_blank" rel="noreferrer" className="text-[#F5551E] inline-flex items-center gap-0.5">{b.website}<ExternalLink size={10} /></a></> : null}</p>
                </div>
                <Button variant="outline" onClick={() => removeIds([b.user_id])} disabled={working} data-testid={`business-remove-${b.user_id}`} className="h-8 rounded-xl text-red-600 border-red-200 hover:bg-red-50 gap-1.5 text-xs shrink-0"><Trash2 size={13} /> Remove</Button>
              </div>
            ))}
          </div>
        </>
      )}

      <Dialog open={confirmOpen} onOpenChange={(o) => !o && setConfirmOpen(false)}>
        <DialogContent className="sm:max-w-md bg-white" data-testid="business-cleanup-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[#12234A] flex items-center gap-2"><Trash2 size={18} className="text-red-600" /> Remove from directory</DialogTitle>
            <DialogDescription className="text-slate-500">
              This drops the Verified Business badge from {selectedIds.length} account{selectedIds.length === 1 ? "" : "s"}, removing {selectedIds.length === 1 ? "it" : "them"} from the public Business Directory. The user account and all listings stay intact. They can re-apply anytime.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setConfirmOpen(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={() => removeIds(selectedIds)} disabled={working} data-testid="business-cleanup-confirm" className="rounded-xl bg-red-600 hover:bg-red-700 text-white gap-1.5">{working ? "Removing…" : <><Trash2 size={15} /> Remove {selectedIds.length}</>}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DocThumb({ path }) {
  const [failed, setFailed] = useState(false);
  const isPdf = (path || "").toLowerCase().endsWith(".pdf");
  if (isPdf || failed) {
    return (
      <div className="h-24 w-32 rounded-xl bg-slate-100 flex flex-col items-center justify-center text-slate-500 group-hover:text-[#F5551E]">
        <FileText size={26} /><span className="text-xs mt-1">Open document</span>
      </div>
    );
  }
  return <img src={photoUrl(path)} alt="document" onError={() => setFailed(true)} className="h-24 w-32 rounded-xl object-cover bg-slate-100" />;
}

export function VerificationQueue() {
  const [data, setData] = useState(null);
  const [reviewFor, setReviewFor] = useState(null); // {req, action}
  const [note, setNote] = useState("");
  const [duration, setDuration] = useState("");
  const [savingDur, setSavingDur] = useState(false);
  const load = () => api.get("/admin/verification").then((r) => setData(r.data)).catch((e) => toast.error(errText(e)));
  const loadDuration = () => api.get("/admin/verification-settings").then((r) => setDuration(String(r.data.duration_days))).catch(() => {});
  useEffect(() => { load(); loadDuration(); }, []);

  const saveDuration = async () => {
    setSavingDur(true);
    try {
      const { data: r } = await api.put("/admin/verification-settings", { duration_days: parseInt(duration, 10) || 0 });
      setDuration(String(r.duration_days));
      toast.success(r.duration_days === 0 ? "Badges now never expire" : `Badges expire after ${r.duration_days} days`);
    } catch (e) { toast.error(errText(e)); }
    finally { setSavingDur(false); }
  };

  const submit = async () => {
    try {
      await api.post(`/admin/verification/${reviewFor.req.id}/review`, { action: reviewFor.action, note: note.trim() });
      toast.success(reviewFor.action === "approve" ? "Seller verified & notified" : "Request rejected & seller notified");
      setReviewFor(null); setNote(""); load();
    } catch (e) { toast.error(errText(e)); }
  };

  const durBar = (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between" data-testid="verification-duration-bar">
      <div>
        <p className="font-semibold text-[#12234A] text-sm flex items-center gap-1.5"><ShieldCheck size={16} className="text-[#F5551E]" /> Badge validity period</p>
        <p className="text-xs text-slate-500 mt-0.5">How long an approved badge stays active before members must re-verify. Set 0 for never-expire.</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Input type="number" min="0" max="3650" value={duration} onChange={(e) => setDuration(e.target.value)} data-testid="verification-duration-input" className="h-9 w-24 rounded-xl" />
        <span className="text-sm text-slate-500">days</span>
        <Button onClick={saveDuration} disabled={savingDur} data-testid="verification-duration-save" className="h-9 rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white">{savingDur ? "…" : "Save"}</Button>
      </div>
    </div>
  );

  if (!data) return <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}</div>;
  if (!data.requests.length) return (
    <div className="space-y-3">
      {durBar}
      <BusinessCleanup />
      <div className="text-center py-20 bg-white rounded-2xl border border-slate-200"><ShieldCheck size={40} className="mx-auto text-slate-300" /><p className="text-slate-500 mt-3">No verification requests yet.</p></div>
    </div>
  );

  const badge = (s) => {
    const map = { pending: "bg-amber-100 text-amber-700", approved: "bg-emerald-100 text-emerald-700", rejected: "bg-red-100 text-red-700" };
    return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${map[s] || map.pending}`}>{s}</span>;
  };

  return (
    <div className="space-y-3">
      {durBar}
      <BusinessCleanup />
      {data.requests.map((r) => (
        <div key={r.id} className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-col sm:flex-row gap-4" data-testid={`verification-${r.id}`}>
          <a href={photoUrl(r.document)} target="_blank" rel="noreferrer" className="shrink-0 group" data-testid={`verification-doc-${r.id}`}>
            <DocThumb path={r.document} />
          </a>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={r.category === "breeder" ? "bg-purple-600 text-white" : r.category === "business" ? "bg-amber-500 text-white" : "bg-emerald-600 text-white"} data-testid={`verification-category-${r.id}`}>{r.category === "breeder" ? "Verified Breeder" : r.category === "business" ? "Verified Business" : "Verified Seller"}</Badge>
              <Badge className={sellerTypeClass(r.seller_type)}>{r.seller_type}</Badge>
              {badge(r.status)}
              {r.category === "business" && r.user_verified_business && <span className="text-xs text-emerald-600 font-semibold inline-flex items-center gap-1"><BadgeCheck size={13} /> now verified</span>}
              {r.category === "seller" && r.user_verified_seller && <span className="text-xs text-emerald-600 font-semibold inline-flex items-center gap-1"><BadgeCheck size={13} /> now verified</span>}
            </div>
            <p className="font-semibold text-[#12234A] mt-1.5">{r.business_name || r.full_name || "—"}</p>
            <p className="text-xs text-slate-400">{r.user_name} · {r.user_email}</p>
            {r.website && <a href={r.website} target="_blank" rel="noreferrer" className="text-xs text-[#F5551E] inline-flex items-center gap-1 mt-1"><Globe size={12} /> {r.website}</a>}
            {r.status !== "pending" && r.note && <p className="text-xs text-slate-500 mt-1">Note: {r.note}</p>}
          </div>
          {r.status === "pending" && (
            <div className="flex sm:flex-col gap-2 shrink-0">
              <Button onClick={() => { setReviewFor({ req: r, action: "approve" }); setNote(""); }} data-testid={`verify-approve-${r.id}`} className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"><Check size={15} /> Approve</Button>
              <Button onClick={() => { setReviewFor({ req: r, action: "reject" }); setNote(""); }} variant="outline" data-testid={`verify-reject-${r.id}`} className="rounded-xl text-red-600 gap-1.5"><X size={15} /> Reject</Button>
            </div>
          )}
        </div>
      ))}
      <Dialog open={!!reviewFor} onOpenChange={(o) => !o && setReviewFor(null)}>
        <DialogContent className="sm:max-w-md bg-white" data-testid="verification-review-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[#12234A] flex items-center gap-2">
              {reviewFor?.action === "approve" ? <BadgeCheck size={18} className="text-emerald-600" /> : <X size={18} className="text-red-600" />}
              {reviewFor?.action === "approve" ? "Approve verification" : "Reject verification"}
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              {reviewFor?.action === "approve"
                ? "The seller gets the verified badge and a confirmation message. Add an optional note."
                : "The seller is notified with your reason so they can reapply."}
            </DialogDescription>
          </DialogHeader>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="verification-note" rows={3} maxLength={500}
            placeholder={reviewFor?.action === "approve" ? "Optional note to the seller…" : "Reason for rejection (recommended)…"} className="rounded-xl resize-none" />
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setReviewFor(null)} className="rounded-xl">Cancel</Button>
            <Button onClick={submit} data-testid="verification-review-confirm" className={`rounded-xl text-white gap-1.5 ${reviewFor?.action === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}`}>
              {reviewFor?.action === "approve" ? <><Check size={15} /> Approve & notify</> : <><X size={15} /> Reject & notify</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
